/**
 * usePartyDialog – save flow
 *
 * Extracted save handler and orchestration helpers. Keeps usePartyDialog.ts
 * under the max-lines limit while preserving public hook behaviour.
 */

import { useCallback } from 'react';

import type { Party } from '@/gen/proto';

import { type PartyFormData } from './party-dialog-helpers';
import {
  flushPendingForms,
  resolveEntityId,
  extractAddressIdsFromList,
  createNewEntity,
  savePartyCreate,
  savePartyUpdate,
  handleSaveError,
  type CountryIdentifier,
} from './usePartyDialog-helpers';
import { getRoleDefinition } from '../../../constants/role-definitions';
import {
  updateEntity,
  type Entity,
} from '../../../services/entity-service';
import { setPartyRoles } from '../../../services/party-service';
import { listRoleDefinitions, createRoleDefinition } from '../../../services/role-definitions-service';

import type { useToast } from '../../ui';

type AddToast = ReturnType<typeof useToast>['addToast'];

interface SaveState {
  formData: PartyFormData;
  selectedEntity: Entity | null;
  isCurrencyConfirmVisible: boolean;
  setShowCurrencyConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  setDuplicateEntities: React.Dispatch<React.SetStateAction<Entity[]>>;
  setDuplicateEntitiesParties: React.Dispatch<React.SetStateAction<Map<string, Party[]>>>;
  setShowDuplicateWarning: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface UseSaveHandlerArgs {
  state: SaveState;
  clearLocalStorage: () => void;
  fetchPartiesForEntity: (id: string) => Promise<Party[]>;
  orgId: string;
  ledgerId: string;
  editingParty?: Party | null;
  countryIdentifiers: CountryIdentifier[];
  currencyOptionsLength: number;
  addToast: AddToast;
  validate: () => boolean;
  onSuccess: (party: Party) => void;
  onClose: () => void;
}

export function useSaveHandler(args: UseSaveHandlerArgs): () => Promise<void> {
  const {
    state, clearLocalStorage, fetchPartiesForEntity, orgId, ledgerId, editingParty,
    countryIdentifiers, currencyOptionsLength, addToast, validate, onSuccess, onClose,
  } = args;

  return useCallback(async (): Promise<void> => {
    if (!validate()) {
      addToast({ variant: 'error', message: 'Please fix validation errors' });
      return;
    }

    if (!editingParty && currencyOptionsLength > 1 && !state.isCurrencyConfirmVisible) {
      state.setShowCurrencyConfirm(true);
      return;
    }
    state.setShowCurrencyConfirm(false);

    state.setSaving(true);
    try {
      const fd = flushPendingForms(state.formData);
      if (fd.roles.length > 0) {
        await ensureRoleDefinitionsExist(orgId, fd.roles).catch(() => { /* non-blocking */ });
      }
      await runSaveFlow({
        fd, orgId, ledgerId, editingParty,
        selectedEntity: state.selectedEntity, countryIdentifiers,
        addToast, onSuccess, onClose, clearLocalStorage,
      });
    } catch (error: unknown) {
      await handleSaveError({
        error, fetchPartiesForEntity,
        setDuplicateEntities: state.setDuplicateEntities,
        setDuplicateEntitiesParties: state.setDuplicateEntitiesParties,
        setShowDuplicateWarning: state.setShowDuplicateWarning,
        addToast,
      });
    } finally {
      state.setSaving(false);
    }
  }, [
    validate, editingParty, currencyOptionsLength, state, orgId, ledgerId,
    countryIdentifiers, addToast, onSuccess, onClose,
    clearLocalStorage, fetchPartiesForEntity,
  ]);
}

interface RunSaveFlowArgs {
  fd: PartyFormData;
  orgId: string;
  ledgerId: string;
  editingParty?: Party | null;
  selectedEntity: Entity | null;
  countryIdentifiers: CountryIdentifier[];
  addToast: AddToast;
  onSuccess: (party: Party) => void;
  onClose: () => void;
  clearLocalStorage: () => void;
}

async function runSaveFlow(args: RunSaveFlowArgs): Promise<void> {
  const { fd, orgId, ledgerId, editingParty, selectedEntity, countryIdentifiers, addToast, onSuccess, onClose, clearLocalStorage } = args;
  const entityId = resolveEntityId(editingParty, fd, selectedEntity);
  const addrIds = extractAddressIdsFromList(fd.addresses);

  if (!entityId) {
    await createEntityAndParty({
      fd, orgId, ledgerId, countryIdentifiers,
      addrIds, addToast, onSuccess, onClose, clearLocalStorage,
    });
    return;
  }

  if (editingParty) {
    await updateExistingParty({
      fd, orgId, editingParty, entityId, addrIds, countryIdentifiers,
      addToast, onSuccess, onClose,
    });
    return;
  }

  await createPartyForExistingEntity({
    fd, orgId, ledgerId, entityId, addrIds, countryIdentifiers,
    addToast, onSuccess, onClose, clearLocalStorage,
  });
}

interface CreateEntityAndPartyArgs {
  fd: PartyFormData;
  orgId: string;
  ledgerId: string;
  countryIdentifiers: CountryIdentifier[];
  addrIds: { billing?: string; shipping?: string };
  addToast: AddToast;
  onSuccess: (party: Party) => void;
  onClose: () => void;
  clearLocalStorage: () => void;
}

async function createEntityAndParty(args: CreateEntityAndPartyArgs): Promise<void> {
  const { fd, orgId, ledgerId, countryIdentifiers, addrIds, addToast, onSuccess, onClose, clearLocalStorage } = args;
  const created = await createNewEntity(fd, countryIdentifiers);
  if (!created.entityId) { throw new Error('Failed to create entity'); }
  const billingAddressId = created.billingAddressId ?? addrIds.billing;
  const shippingAddressId = created.shippingAddressId ?? addrIds.shipping;

  const saved = await savePartyCreate({
    orgId, ledgerId, formData: fd,
    entityId: created.entityId, billingAddressId, shippingAddressId, countryIdentifiers,
  });
  addToast({ variant: 'success', message: `${fd.entityName} created successfully` });
  clearLocalStorage();
  onSuccess(saved);
  onClose();
}

interface UpdateExistingPartyArgs {
  fd: PartyFormData;
  orgId: string;
  editingParty: Party;
  entityId: string;
  addrIds: { billing?: string; shipping?: string };
  countryIdentifiers: CountryIdentifier[];
  addToast: AddToast;
  onSuccess: (party: Party) => void;
  onClose: () => void;
}

async function updateExistingParty(args: UpdateExistingPartyArgs): Promise<void> {
  const { fd, orgId, editingParty, entityId, addrIds, countryIdentifiers, addToast, onSuccess, onClose } = args;
  const billingAddressId = addrIds.billing ?? editingParty.billingAddressId ?? '';
  const shippingAddressId = addrIds.shipping ?? editingParty.shippingAddressId ?? '';

  const saved = await savePartyUpdate({
    orgId, editingParty, formData: fd,
    entityId, billingAddressId, shippingAddressId, countryIdentifiers,
  });
  if (editingParty.entityId) {
    await updateEntity(editingParty.entityId, { bankAccounts: fd.bankAccounts });
  }
  await updateRolesIfChanged({ orgId, editingParty, fd, addToast });

  addToast({ variant: 'success', message: `${fd.entityName} updated successfully` });
  onSuccess(saved);
  onClose();
}

async function updateRolesIfChanged(args: {
  orgId: string;
  editingParty: Party;
  fd: PartyFormData;
  addToast: AddToast;
}): Promise<void> {
  const { orgId, editingParty, fd, addToast } = args;
  const prevRoles = [...(editingParty.roles ?? [])].sort();
  const nextRoles = [...fd.roles].sort();
  const didRolesChange = prevRoles.join(',') !== nextRoles.join(',');
  if (!didRolesChange || fd.roles.length === 0) { return; }

  await setPartyRoles(orgId, editingParty.id, fd.roles).catch(err => {
    addToast({
      variant: 'warning',
      message: `Party updated but roles could not be saved: ${err instanceof Error ? err.message : 'unknown error'}`,
    });
  });
}

interface CreatePartyForExistingEntityArgs {
  fd: PartyFormData;
  orgId: string;
  ledgerId: string;
  entityId: string;
  addrIds: { billing?: string; shipping?: string };
  countryIdentifiers: CountryIdentifier[];
  addToast: AddToast;
  onSuccess: (party: Party) => void;
  onClose: () => void;
  clearLocalStorage: () => void;
}

async function createPartyForExistingEntity(args: CreatePartyForExistingEntityArgs): Promise<void> {
  const { fd, orgId, ledgerId, entityId, addrIds, countryIdentifiers, addToast, onSuccess, onClose, clearLocalStorage } = args;
  const saved = await savePartyCreate({
    orgId, ledgerId, formData: fd, entityId,
    billingAddressId: addrIds.billing,
    shippingAddressId: addrIds.shipping,
    countryIdentifiers,
  });
  addToast({ variant: 'success', message: `${fd.entityName} created successfully` });
  clearLocalStorage();
  onSuccess(saved);
  onClose();
}

async function ensureRoleDefinitionsExist(orgId: string, roles: string[]): Promise<void> {
  const result = await listRoleDefinitions(orgId);
  const existing = new Set((result.data ?? []).map(r => r.role));
  const missing = roles.filter(r => !existing.has(r));
  if (missing.length === 0) { return; }

  await Promise.all(
    missing.map(role => {
      const def = getRoleDefinition(role);
      if (!def) { return Promise.resolve(); }
      return createRoleDefinition(orgId, {
        role: def.role,
        label: def.label,
        accountMappings: def.accountMappings,
        billing: def.billing,
      });
    }),
  );
}
